import React, { useEffect, useState } from 'react';
import { Text } from 'drei';
import { useMedia, useTexture } from '../../../hooks';
import { ReactThreeFiber, useThree } from 'react-three-fiber';
import { useGesture } from 'react-use-gesture';
import { useSpring, a, config } from 'react-spring/three';
import { fontFamilyNormal, textAlignCenter } from '../../../config/ui';
import { assignParametersToNativeEvent } from '../../../common/utils';
import { MediaEvents } from '../../../common/media';
import Button from '../button';
import qualityBtnIcon from '../../../assets/images/xr/icons/quality.png';

type QualityProps = {
  target: string;
  position?: ReactThreeFiber.Vector3;
  // width?: number;
  // height?: number;
};

type qualityItem = {
  id: number;
  value: string;
};

type QualityListItemProps = {
  data: qualityItem;
  index: number;
  totalCount: number;
  active: boolean;
  onClick: (lvl: qualityItem) => void;
};

const QualityListItem = ({ data, index, totalCount, active, onClick }: QualityListItemProps) => {
  const mainBg = '#2c2b2c';
  const activeBg = '#373637';

  const { gl } = useThree();

  const [spring, set] = useSpring(() => ({
    color: mainBg,
    config: config.gentle,
  }));

  useEffect(() => {
    set({ color: active ? activeBg : mainBg });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  const bind = useGesture({
    onClick: ({ args: [lvl], event }) => {
      const evt = event as any;
      evt.nativeEvent.preventDefault();
      evt.nativeEvent.stopPropagation();
event.stopPropagation();
      onClick(lvl);
    },
    onHover: ({ hovering, event }) => {
      const evt = event as any;
      evt.nativeEvent.preventDefault();
      evt.nativeEvent.stopPropagation();
event.stopPropagation();

      if (hovering) {
        setTimeout(() => {
          gl.domElement.classList.add('cursor-pointer');
        });
      } else {
        gl.domElement.classList.remove('cursor-pointer');
      }

      set({ color: hovering || active ? activeBg : mainBg });
    },
  });

  const width = 0.07;
  const height = 0.025;

  const originalEvents = bind(data);

  return (
    <a.mesh
      position={[0, height * (totalCount - index), 0]}
      {...assignParametersToNativeEvent(originalEvents, ['onClick'], [data])}>
      <planeBufferGeometry args={[width, height]} attach="geometry" />
      <a.meshBasicMaterial attach="material" {...spring} />
      <Text
        position={[0, 0, 0.0001]}
        font={fontFamilyNormal}
        color="#fff"
        fontSize={0.013}
        maxWidth={width}
        lineHeight={1}
        textAlign={textAlignCenter}
        anchorX="center"
        anchorY="middle">
        {data.value}
      </Text>
    </a.mesh>
  );
};

const Quality = ({ target, position }: QualityProps) => {
  const media = useMedia(target);
  const [{ list, selected, show }, setState] = useState<{
    list: qualityItem[];
    selected: number | undefined;
    show: boolean;
  }>({
    list: [],
    selected: undefined,
    show: false,
  });

  const { texture: qualityBtnTxt } = useTexture(qualityBtnIcon);

  useEffect(() => {
    if (!media) return;

    const handleReady = () => {
      const levels = media.getLevels();
      const current = media.getCurrentLevel();

      const list = [{ id: -1, value: 'auto' }, ...levels.map((_, i) => ({ id: i, value: `${_.width}p` }))].reverse();
      const selected = list.find(_ => _.id === current)?.id;

      setState(state => ({
        ...state,
        list,
        selected,
      }));
    };

    const handleChangeLevel = (lvlId: number) => {
      setState(state => ({
        ...state,
        selected: lvlId,
      }));
    };

    media.on(MediaEvents.Ready, handleReady);
    media.on(MediaEvents.Change_Level, handleChangeLevel);

    return () => {
      media.off(MediaEvents.Ready, handleReady);
      media.off(MediaEvents.Change_Level, handleChangeLevel);
    };
  }, [media]);

  return (
    <group position={position}>
      <Button
        onClick={() => setState(state => ({ ...state, show: !state.show }))}
        defaultState={{
          main: { material: { transparent: true, map: qualityBtnTxt }, size: 0.02 },
        }}
      />

      {show ? (
        <group position={[-0.005, 0.02, 0.0001]}>
          {list.map((lvl, i) => {
            return (
              <QualityListItem
                key={lvl.id}
                data={lvl}
                index={i}
                active={selected === lvl.id}
                totalCount={list.length}
                onClick={lvl => {
                  media?.setLevel(lvl.id);
                }}
              />
            );
          })}
        </group>
      ) : null}
    </group>
  );
};

export default Quality;
